import React, { useEffect, useMemo, useState } from "react";
import useApiHandler from "../hooks/ApiHandler";
import { LegalApi } from "../../service";
import PdfViewerModal from "../components/common/PdfViewerModal";

const LegalPage = () => {
  const { callApi, loading } = useApiHandler();
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const fetchDocuments = async () => {
    setError(null);
    try {
      await callApi(() => LegalApi.getDocuments(), {
        onSuccess: (response) => {
          setDocuments(response?.data || []);
        },
      });
    } catch (err) {
      setError("Unable to load legal documents. Please try again later.");
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tocEntries = useMemo(
    () =>
      documents.map((doc) => ({
        id: `legal-${doc.id}`,
        name: doc.name,
      })),
    [documents]
  );

  const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-[calc(100vh-511px)] flex flex-col bg-white">
      <div className="max-w-6xl mx-auto px-4 py-12 pt-[130px] flex-1 w-full">
        <div className="text-center mb-10">
          <p className="text-[#3CAB5C] text-sm font-semibold uppercase tracking-[0.2em] mb-3">
            Legal
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#242424] mb-4">
            Properteasy Legal Center
          </h1>
          {/*<p className="text-[#595959] text-base md:text-lg max-w-3xl mx-auto">*/}
          {/*  Access our Terms of Service, Privacy Policy, and other legal documents in one place.*/}
          {/*</p>*/}
        </div>

        <div className="grid gap-6 md:grid-cols-[260px_1fr]">
          <aside className="border border-[#ECECEC] rounded-2xl p-6 sticky top-24 h-fit bg-white">
            <h2 className="text-lg font-semibold text-[#242424] mb-4">
              Table of Contents
            </h2>
            {tocEntries.length ? (
              <ul className="space-y-3">
                {tocEntries.map((entry) => (
                  <li key={entry.id}>
                    <a
                      href={`#${entry.id}`}
                      className="text-[#3CAB5C] font-medium text-sm hover:underline"
                    >
                      {entry.name}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#595959]">
                Documents will appear here once available.
              </p>
            )}
          </aside>

          <section className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {loading && <div className="text-[#595959]">Loading documents…</div>}

            {!loading && !documents.length && !error && (
              <div className="text-[#595959]">
                No legal documents are available yet.
              </div>
            )}

            {documents.map((doc) => (
              <article
                key={doc.id}
                id={`legal-${doc.id}`}
                className="border border-[#ECECEC] rounded-2xl p-6 bg-white"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                  <h3 className="text-2xl font-semibold text-[#242424]">
                    {doc.name}
                  </h3>
                  {doc.uploaded_at ? (
                    <p className="text-sm text-[#595959]">
                      Last updated {formatDate(doc.uploaded_at)}
                    </p>
                  ) : null}
                </div>
                <p className="text-sm text-[#595959] mb-4">
                  View the latest version of our {doc.name}.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDoc(doc);
                      setIsViewerOpen(true);
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-[#3CAB5C] text-white text-sm font-semibold"
                  >
                    View Document
                  </button>
                </div>
              </article>
            ))}
          </section>
        </div>
      </div>
      <PdfViewerModal
        open={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        documentTitle={selectedDoc?.name}
        documentUrl={selectedDoc?.document_url || selectedDoc?.document}
        updatedAt={selectedDoc?.uploaded_at}
      />
    </div>
  );
};

export default LegalPage;
